import { Stack, Grid, Typography } from '@mui/material';

import { useCallback, useEffect, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import _ from 'lodash';

import { ReactComponent as BackIcon } from 'common/assets/images/button-previous.svg';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	AI_SIMULATION_MODEL_KEYS,
	AI_SIMULATION_TYPES,
	AI_TYPE,
} from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonShareDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';

import { genPromptRegulationSummary, genRegulationSummary } from '../api';
import { IAIRegulationSummaryInput, IAIModelDetails } from '../api/type-def';
import { aiModelPDFDownload } from '../Components/AIModelPDFDownload/AIModelPDFDownload';
import AIModelSave from '../Components/AIModelSave/AIModelSave';
import AIShare from '../Components/AIShare/AIShare';
import GeneratedTextArea from '../Components/GeneratedTextArea/GeneratedTextArea';
import StepHeader from '../Components/StepHeader/StepHeader';
import useInputOutput from '../hooks/useInputOutput';
import {
	toSentenceCase,
	useNavigate,
	navigateToRegulationControl,
} from '../SimulationArena/SimulationArenaModules';

import RegulationSummaryInput from './RegulationSummaryInput';

import styles from './RegulationSummaryInput.module.scss';

const RegulationSummaryPage = () => {
	const { REGULATION_SUMMARY, REGULATION_SUMMARY_BUTTON } = AI_SIMULATION_TYPES;
	const { MODEL } = AI_TYPE;
	const { REGULATION_SUMMARY_KEY } = AI_SIMULATION_MODEL_KEYS;
	const {
		inputTxt,
		updateInput,
		outputResult,
		handleGenerate,
		updateOutput,
		handlePromptSelect,
		isGenerating,
	} = useInputOutput({
		aiModelKey: REGULATION_SUMMARY_KEY,
		workflowStep: REGULATION_SUMMARY,
		reqPromptSelect: genPromptRegulationSummary,
		requestFn: genRegulationSummary,
	});

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const selectedRegSummaryAIModel = useSelector(
		(state: RootState) => state?.simulationArena?.selectedAIModel,
	);

	const selectedRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedRecipe,
	);

	const institutionValue = useSelector(
		(state: RootState) => state.simulationArena.simulationInstitutionValue,
	);

	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	useEffect(() => {
		if (regulationText) {
			updateInput(regulationText);
		}
	}, [regulationText]);

	const selectedStatementItem = useMemo(() => {
		if (_.isEmpty(selectedRegSummaryAIModel)) return undefined;
		const inputItems =
			selectedRegSummaryAIModel.input as IAIRegulationSummaryInput;
		return {
			institution: inputItems.institution,
		};
	}, [selectedRegSummaryAIModel]);

	useEffect(() => {
		if (selectedRegSummaryAIModel.input && selectedRegSummaryAIModel.output) {
			const input =
				selectedRegSummaryAIModel.input as IAIRegulationSummaryInput;
			updateInput(input.inputTxt);
			updateOutput(selectedRegSummaryAIModel.output);
		}
	}, [selectedRegSummaryAIModel]);

	const aiModelData: IAIModelDetails = {
		...(selectedRegSummaryAIModel?.id
			? { id: selectedRegSummaryAIModel.id }
			: {}),
		name: selectedRegSummaryAIModel?.name || '',
		input: {
			inputTxt,
			institution: institutionValue.trim(),
		},
		output: outputResult,
		workflowStep: REGULATION_SUMMARY,
		type: MODEL,
	};
	const fileDownload = () => {
		aiModelPDFDownload(
			_.isEmpty(selectedRegSummaryAIModel)
				? null
				: selectedRegSummaryAIModel.name,
			REGULATION_SUMMARY,
			REGULATION_SUMMARY,
			{
				inputTitle: 'Insert regulation text',
				inputValue: inputTxt,
			},
			{
				outputTitle: REGULATION_SUMMARY,
				outputValue: outputResult,
			},
		);
	};

	const handleShare = () => {
		dispatch(setSimulationCommonShareDrawer(true));
	};

	const backButtonHandler = () => {
		dispatch(setWorkflowFeedback([]));
		navigateToRegulationControl(navigate, regulationText);
	};

	return (
		<>
			<Stack>
				<StepHeader title={REGULATION_SUMMARY} />

				<Stack gap={3}>
					{selectedRecipe && (
						<Grid>
							<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
								<Stack
									direction="row"
									className={styles['btn-back-navigation']}
									onClick={backButtonHandler}
								>
									<BackIcon />
									<Typography className={styles['back-button-text']}>
										Back to Search Database
									</Typography>
								</Stack>
							</Grid>
						</Grid>
					)}
					<GeneratedTextArea
						category={MODEL}
						title="Insert regulation text"
						onChange={updateInput}
						button={{
							text: `${
								outputResult ? 'Regenerate' : 'Generate'
							} ${REGULATION_SUMMARY_BUTTON}`,
							aiModelKey: REGULATION_SUMMARY_KEY,
							type: outputResult ? 'tertiary' : 'primary',
							loading: isGenerating,
							onPromptChange: handlePromptSelect,
							onClick: handleGenerate,
						}}
						inputElement={
							<RegulationSummaryInput items={selectedStatementItem} />
						}
						textValue={inputTxt}
					/>

					{outputResult && (
						<GeneratedTextArea
							category={MODEL}
							title={toSentenceCase(REGULATION_SUMMARY)}
							dataType="output"
							textValue={outputResult}
							inputData={inputTxt}
							downloadHandler={fileDownload}
							onChange={updateOutput}
							isSaveIconVisible={false}
							shareHandler={handleShare}
							isRegenerating={isGenerating}
						/>
					)}
				</Stack>
				{outputResult && <AIModelSave aiModeldata={aiModelData} />}
			</Stack>
			<AIShare aiSharedData={aiModelData} type={MODEL} />
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</>
	);
};

export default RegulationSummaryPage;
